import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// Bootstrap 4 modal component for React
const Modal = ({
	id,
	title,
	children,
	onClose,
	isOpen,
	size = '', // Options: 'sm', 'lg', 'xl' or empty for default
	scrollable = false,
}) => {
	const modalRef = useRef(null);
	const isShownRef = useRef(false);

	useEffect(() => {
		const $ = window.$;
		if (!$ || !$.fn.modal || !modalRef.current) return;

		if (isOpen && !isShownRef.current) {
			try {
				// Show the modal
				$(modalRef.current).modal({
					backdrop: 'static', // Prevent closing on backdrop click
					keyboard: false, // Prevent closing on escape key
				});
				$(modalRef.current).modal('show');
				isShownRef.current = true;

				// Add event listener for when modal is hidden
				$(modalRef.current).on('hidden.bs.modal', () => {
					isShownRef.current = false;
					if (onClose) onClose();
				});
			} catch (error) {
				console.error('Error showing modal:', error);
				if (onClose) onClose();
			}
		} else if (!isOpen && isShownRef.current) {
			try {
				$(modalRef.current).modal('hide');
				// Safety cleanup to ensure backdrop/body are cleared even if the event doesn't fire
				setTimeout(() => {
					$('.modal-backdrop').remove();
					$('body').removeClass('modal-open');
				}, 150);
				isShownRef.current = false;
			} catch (error) {
				console.error('Error hiding modal:', error);
			}
		}
	}, [isOpen, onClose]);

	// Cleanup when component unmounts
	useEffect(() => {
		return () => {
			const $ = window.$;
			if ($ && $.fn.modal && modalRef.current) {
				try {
					$(modalRef.current).off('hidden.bs.modal');
					if (isShownRef.current) {
						$(modalRef.current).modal('hide');
						isShownRef.current = false;
					}
					// Clean up any leftover modal artifacts immediately
					$('.modal-backdrop').remove();
					$('body').removeClass('modal-open');
				} catch (error) {
					console.error('Error cleaning up modal:', error);
				}
			}
		};
	}, []);

	const handleClose = (e) => {
		e.preventDefault();
		e.stopPropagation();
		const $ = window.$;
		if ($ && $.fn.modal && modalRef.current) {
			// For Bootstrap 4.6, we need to handle the backdrop cleanup more explicitly
			$(modalRef.current).modal('hide');
			isShownRef.current = false;
			// Force backdrop removal after a short delay to ensure animation completes
			setTimeout(() => {
				$('.modal-backdrop').remove();
				$('body').removeClass('modal-open');
			}, 150); // Reduced timeout for Bootstrap 4.6
		}
		if (onClose) {
			onClose();
		}
	};

	const handleModalClick = (e) => {
		// Prevent modal from closing when clicking inside modal content
		e.stopPropagation();
	};

	const handleBackdropClick = (e) => {
		// Only close if clicking on the backdrop itself, not modal content
		if (e.target === e.currentTarget) {
			handleClose(e);
		}
	};

	return createPortal(
		<div
			className="modal fade"
			id={id}
			ref={modalRef}
			tabIndex="-1"
			role="dialog"
			aria-labelledby={`${id}Label`}
			aria-hidden="true"
			onClick={handleBackdropClick}
			style={{ display: isOpen ? undefined : 'none' }}
		>
			<div
				className={`modal-dialog ${size ? `modal-${size}` : ''} ${
					scrollable ? 'modal-dialog-scrollable' : ''
				}`}
				role="document"
				onClick={handleModalClick}
			>
				<div className="modal-content">
					<div className="modal-header">
						<h5 className="modal-title" id={`${id}Label`}>
							{title}
						</h5>
						<button
							type="button"
							className="close"
							aria-label="Close"
							onClick={handleClose}
						>
							<span aria-hidden="true">&times;</span>
						</button>
					</div>
					<div className="modal-body">{children}</div>
				</div>
			</div>
		</div>,
		document.body
	);
};

export default Modal;
